import { useMemo } from 'react'
import { DailyTotals } from '@/types'
import { PlanData } from './useStudioAnalytics'

export type ChartMode = 'volume' | 'conversion'
export type ChartView = 'daily' | 'cumulative'
export type ConversionWindow = 1 | 3 | 7

export interface ChartDataPoint {
  date: string
  fullDate: string
  // Daily values
  revenue: number
  newRevenue: number
  existingRevenue: number
  visitors: number
  sales: number
  leads: number
  appointments: number
  // Cumulative values
  cumLeads: number
  cumAppointments: number
  cumVisitors: number
  cumSales: number
  cumRevenue: number
  cumNewRevenue: number
  cumExistingRevenue: number
  // Plan lines
  planLeads: number | null
  planAppointments: number | null
  planVisitors: number | null
  planSales: number | null
  planRevenue: number | null
  planNewRevenue: number | null
  planExistingRevenue: number | null
  // Conversions
  cr1: number
  cr2: number
  cr3: number
  crTotal: number
}

export interface UseChartDataParams {
  dailyData: DailyTotals[]
  conversionDailyData?: DailyTotals[]
  planData: PlanData | null
  conversionWindow: ConversionWindow
}

export interface UseChartDataReturn {
  chartData: ChartDataPoint[]
}

/**
 * Форматирование даты для отображения на графике
 */
const formatChartDate = (dateString: string): string => {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}.${month}`
}

/**
 * Hook для подготовки данных для графиков
 * Трансформирует дневные данные в формат для recharts с расчетом:
 * - Кумулятивных значений
 * - Плановых линий
 * - Скользящих конверсий
 */
export function useChartData({
  dailyData,
  conversionDailyData,
  planData,
  conversionWindow
}: UseChartDataParams): UseChartDataReturn {
  
  const chartData = useMemo((): ChartDataPoint[] => {
    if (!dailyData.length) {
      return []
    }

    const conversionSourceData = (conversionDailyData && conversionDailyData.length > 0)
      ? conversionDailyData
      : dailyData
    const conversionIndexByDate = new Map<string, number>()
    conversionSourceData.forEach((day, idx) => {
      conversionIndexByDate.set(day.metric_date, idx)
    })

    return dailyData.map((item, index, array) => {
      // Рассчитываем скользящие конверсии за последние N дней
      const windowSize = conversionWindow
      const conversionIndex = conversionIndexByDate.get(item.metric_date) ?? -1
      const conversionWindowEnd = conversionIndex >= 0 ? conversionIndex : index
      const windowStart = Math.max(0, conversionWindowEnd - windowSize + 1)
      const windowData = conversionSourceData.slice(windowStart, conversionWindowEnd + 1)
      
      const sumLeads = windowData.reduce((sum, d) => sum + (d.leads_count || 0), 0)
      const sumAppointments = windowData.reduce((sum, d) => sum + (d.appointments_count || 0), 0)
      const sumVisitors = windowData.reduce((sum, d) => sum + (d.new_visitors_count || 0), 0)
      const sumSales = windowData.reduce((sum, d) => sum + (d.new_sales_count || 0) + (d.new_subscriptions_count || 0), 0)
      
      // Кумулятивные значения (от начала периода до текущего дня включительно)
      const cumulativeData = array.slice(0, index + 1)
      const cumLeads = cumulativeData.reduce((sum, d) => sum + (d.leads_count || 0), 0)
      const cumAppointments = cumulativeData.reduce((sum, d) => sum + (d.appointments_count || 0), 0)
      const cumVisitors = cumulativeData.reduce((sum, d) => sum + (d.new_visitors_count || 0), 0)
      const cumSales = cumulativeData.reduce((sum, d) => sum + (d.new_sales_count || 0) + (d.new_subscriptions_count || 0), 0)
      const cumRevenue = cumulativeData.reduce((sum, d) => sum + (d.gross_revenue || 0), 0)
      const cumNewRevenue = cumulativeData.reduce(
        (sum, d) => sum + (d.new_sales_amount || 0) + (d.new_subscriptions_amount || 0) + (d.membership_fees_new_amount || 0),
        0
      )
      const cumExistingRevenue = cumulativeData.reduce((sum, d) => 
        sum + (
          (d.renewals_amount || 0) +
          (d.returning_clients_amount || 0) +
          (d.upgrades_amount || 0) +
          (d.membership_fees_subscription_upgrades_amount || 0) +
          (d.membership_fees_subscription_returning_amount || 0) +
          (d.subscription_renewals_amount || 0) +
          (d.subscription_upgrades_amount || 0) +
          (d.subscription_returning_clients_amount || 0)
        ),
        0
      )
      
      // Плановые кумулятивные линии (линейное распределение плана по дням периода)
      const totalDays = array.length
      const currentDay = index + 1
      const planProgress = currentDay / totalDays
      
      const planLeads = planData?.leads_count_plan 
        ? Math.round(planData.leads_count_plan * planProgress) 
        : null
      const planAppointments = planData?.appointments_count_plan 
        ? Math.round(planData.appointments_count_plan * planProgress) 
        : null
      const planVisitors = planData?.new_visitors_count_plan 
        ? Math.round(planData.new_visitors_count_plan * planProgress) 
        : null
      const hasPlanSales = (planData?.new_sales_count_plan ?? null) !== null || (planData?.new_subscriptions_count_plan ?? null) !== null
      const planSales = hasPlanSales
        ? Math.round(((planData?.new_sales_count_plan || 0) + (planData?.new_subscriptions_count_plan || 0)) * planProgress)
        : null
      const planRevenue = planData?.total_gross_revenue_plan 
        ? Math.round(planData.total_gross_revenue_plan * planProgress) 
        : null
      const hasPlanNewRevenue =
        (planData?.new_sales_amount_plan ?? null) !== null ||
        (planData?.new_subscriptions_amount_plan ?? null) !== null ||
        (planData?.membership_fees_new_plan ?? null) !== null
      const planNewRevenue = hasPlanNewRevenue
        ? Math.round(
            (
              (planData?.new_sales_amount_plan || 0) +
              (planData?.new_subscriptions_amount_plan || 0) +
              (planData?.membership_fees_new_plan || 0)
            ) * planProgress
          )
        : null
      const hasPlanExistingRevenue = (
        (planData?.renewals_amount_plan ?? null) !== null ||
        (planData?.returning_clients_amount_plan ?? null) !== null ||
        (planData?.upgrades_amount_plan ?? null) !== null ||
        (planData?.subscription_renewals_amount_plan ?? null) !== null ||
        (planData?.subscription_returning_clients_amount_plan ?? null) !== null ||
        (planData?.subscription_upgrades_amount_plan ?? null) !== null
      )
      const planExistingRevenue = hasPlanExistingRevenue
        ? Math.round(
            (
              (planData?.renewals_amount_plan || 0) +
              (planData?.returning_clients_amount_plan || 0) +
              (planData?.upgrades_amount_plan || 0) +
              (planData?.subscription_renewals_amount_plan || 0) +
              (planData?.subscription_returning_clients_amount_plan || 0) +
              (planData?.subscription_upgrades_amount_plan || 0)
            ) * planProgress
          )
        : null
      
      // Расчет конверсий за скользящее окно
      const cr1 = sumLeads > 0 ? (sumAppointments / sumLeads) * 100 : 0
      const cr2 = sumAppointments > 0 ? (sumVisitors / sumAppointments) * 100 : 0
      const cr3 = sumVisitors > 0 ? (sumSales / sumVisitors) * 100 : 0
      const crTotal = sumLeads > 0 ? (sumSales / sumLeads) * 100 : 0

      return {
        date: formatChartDate(item.metric_date || ''),
        fullDate: item.metric_date || '',
        // Daily values
        revenue: item.gross_revenue || 0,
        newRevenue: (item.new_sales_amount || 0) + (item.new_subscriptions_amount || 0) + (item.membership_fees_new_amount || 0),
        existingRevenue: (item.renewals_amount || 0) +
                        (item.returning_clients_amount || 0) +
                        (item.upgrades_amount || 0) +
                        (item.membership_fees_subscription_upgrades_amount || 0) +
                        (item.membership_fees_subscription_returning_amount || 0) +
                        (item.subscription_renewals_amount || 0) +
                        (item.subscription_returning_clients_amount || 0) +
                        (item.subscription_upgrades_amount || 0),
        visitors: item.new_visitors_count || 0,
        sales: (item.new_sales_count || 0) + (item.new_subscriptions_count || 0),
        leads: item.leads_count || 0,
        appointments: item.appointments_count || 0,
        // Cumulative values
        cumLeads,
        cumAppointments,
        cumVisitors,
        cumSales,
        cumRevenue,
        cumNewRevenue,
        cumExistingRevenue,
        // Plan lines
        planLeads,
        planAppointments,
        planVisitors,
        planSales,
        planRevenue,
        planNewRevenue,
        planExistingRevenue,
        // Conversions
        cr1,
        cr2,
        cr3,
        crTotal
      }
    })
  }, [dailyData, conversionDailyData, planData, conversionWindow])

  return {
    chartData
  }
}

/**
 * Вспомогательная функция для фильтрации данных графика по видимым линиям
 */
export function filterVisibleChartData(
  chartData: ChartDataPoint[],
  visibleLines: Record<string, boolean>,
  chartView: ChartView
): ChartDataPoint[] {
  if (chartView === 'cumulative') {
    // Для кумулятивного вида возвращаем данные как есть
    return chartData
  }
  
  // Для дневного вида можем применить дополнительные фильтры если нужно
  return chartData
}

/**
 * Получить список доступных недель в периоде
 */
export function getAvailableWeeks(periodStart: string, periodEnd: string): Array<{ value: string; label: string }> {
  const start = new Date(periodStart)
  const end = new Date(periodEnd)
  const diffTime = end.getTime() - start.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1
  const weeksCount = Math.ceil(diffDays / 7)
  
  const weeks = []
  for (let i = 1; i <= Math.min(weeksCount, 5); i++) {
    weeks.push({ value: `week_${i}`, label: `Неделя ${i}` })
  }
  return weeks
}
